/**
 * menu-tree — TM_MENU(WEB_STUDIO) 조회 결과 훅 + 브레드크럼 보조 (스튜디오_개선_설계서_v3.0
 * §9, FR-27~30).
 *
 * 좌측 내비·하단 고정 영역·플라이아웃(스튜디오 설정/알림/사용자 정보)·우측상단 고객센터를
 * 구성하는 항목(라벨·경로·아이콘·순서·소속 관계)은 더 이상 코드 하드코딩 배열이 아니라
 * 로그인 세션 동안 이 훅으로 조회한 트리를 그대로 렌더한다(LeftNav.tsx/TopBar.tsx가 공유).
 */
import { useQuery } from '@tanstack/react-query';
import { fetchMenus, type MenuNode } from '../shared/api/menus';

export type { MenuNode } from '../shared/api/menus';

export const WEB_STUDIO_PORTAL_CODE = 'WEB_STUDIO';

/**
 * 로그인 직후 한 번 조회해 세션 동안 보관(설계서 §7 조회 시점·캐시) — TM_MENU를 바꾸는
 * 화면은 이 앱 범위 밖이라 런타임 무효화 대상이 없고, 재로그인 시에만 다시 조회한다.
 */
export function useMenuTree(portalCode: string = WEB_STUDIO_PORTAL_CODE) {
  return useQuery({
    queryKey: ['menus', portalCode],
    queryFn: () => fetchMenus(portalCode),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * 알림·현장요약 라우팅 전용 — 전체 포털(WEB_STUDIO뿐 아니라 WEB_PHOTO/WEB_SCHEDULE 등 "할일"
 * 포털까지)을 대상으로 조회한다. webMenuCode가 어느 포털에 등록돼 있든 resolveMenuCodeAction이
 * 찾아낼 수 있어야 하므로, 좌측 내비 렌더링용 useMenuTree(WEB_STUDIO 고정)와는 별도로 둔다.
 */
export function useAllMenuTree() {
  return useQuery({
    queryKey: ['menus', 'ALL'],
    queryFn: () => fetchMenus(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** 최상위 노드 중 positionCode가 일치하는 것만 순서대로 반환 */
export function pickByPosition(tree: MenuNode[] | undefined, position: MenuNode['positionCode']): MenuNode[] {
  return (tree ?? []).filter((n) => n.positionCode === position);
}

/**
 * displayYn='N'인 노드(및 그 하위 전체)를 제거한 트리 — LNB/플라이아웃/할일 등 실제 화면
 * 렌더링 전용으로만 쓴다. 목록→상세 드릴다운처럼 메뉴 목록에는 안 나오지만 알림·딥링크로는
 * 이동해야 하는 화면이 displayYn='N'으로 등록되는데, 라우팅(findByCode/resolveMenuCodeAction/
 * resolveMenuCrumb 등)은 이 필터를 타지 않은 원본 tree를 그대로 써야 그 화면들도 정상 이동·
 * 브레드크럼 표시가 된다 — 렌더링 컴포넌트에서만 이 함수를 거쳐 쓸 것.
 */
export function toVisibleTree(tree: MenuNode[] | undefined): MenuNode[] {
  return (tree ?? [])
    .filter((n) => n.displayYn === 'Y')
    .map((n) => ({ ...n, children: toVisibleTree(n.children) }));
}

/** ICON_KEY가 비었거나 아이콘 파일을 못 불러올 때 쓰는 메뉴 기본 아이콘 */
export const DEFAULT_MENU_ICON_URL = '/icons/lnb/default.svg';

/** TM_MENU.ICON_KEY → 아이콘 URL. 파일명(확장자 제외, 예: settings)이면 /icons/lnb/{key}.svg, 비어 있으면 기본 아이콘. */
export function resolveMenuIconUrl(iconKey: string | null | undefined): string {
  const key = iconKey?.trim();
  return key ? `/icons/lnb/${key}.svg` : DEFAULT_MENU_ICON_URL;
}

/** 트리 전체(하위 포함)에서 code로 하나 찾기 */
export function findByCode(tree: MenuNode[] | undefined, code: string): MenuNode | null {
  const stack = [...(tree ?? [])];
  while (stack.length) {
    const n = stack.shift()!;
    if (n.code === code) return n;
    stack.push(...n.children);
  }
  return null;
}

/**
 * 메뉴 활성·브레드크럼 판정에서 무시하는 '화면 내부 진입' 쿼리 파라미터.
 * 화면을 가르는 파라미터(view 등)는 비교에 남기고, 아래 컨텍스트 파라미터와 알림 딥링크 targetId는 제외한다.
 * 메뉴 routePath가 해당 키를 직접 지정한 경우(예: ?tab=x)에는 그 키를 무시하지 않는다.
 */
export const MENU_MATCH_IGNORED_PARAMS: readonly string[] = ['siteId', 'scheduleId', 'taskId', 'card', 'pop', 'f', 'tab', 'targetId'];

/** 현재 search에서 무시 파라미터를 제외한 비교용 쿼리 문자열(키 정렬). menuQuery는 메뉴 routePath의 query 부분. */
function comparableQuery(search: string, menuQuery?: string): string {
  const own = new URLSearchParams(menuQuery ?? '');
  const cur = new URLSearchParams(search);
  for (const k of MENU_MATCH_IGNORED_PARAMS) if (!own.has(k)) cur.delete(k);
  cur.sort();
  return cur.toString();
}

/** 메뉴 routePath가 현재 위치와 정확히 일치하는지 — query가 있으면 search까지, 없으면 search도 없어야 일치. */
export function isExactRoute(routePath: string, pathname: string, search: string): boolean {
  const [rp, rq] = routePath.split('?');
  if (pathname !== rp) return false;
  if (rq) {
    const want = new URLSearchParams(rq);
    want.sort();
    return comparableQuery(search, rq) === want.toString();
  }
  return comparableQuery(search) === '';
}

/** 메뉴 routePath가 현재 위치 또는 그 상위 경로인지(예: /showroom ⊃ /showroom/inquiries). query가 있으면 정확 일치만 본다. */
export function isRouteOrDescendant(routePath: string, pathname: string, search: string): boolean {
  const [rp, rq] = routePath.split('?');
  if (rq) return isExactRoute(routePath, pathname, search);
  return pathname === rp || pathname.startsWith(`${rp}/`);
}

/**
 * 하위 메뉴를 펼치는 노드(FLYOUT_ROOT 등)의 활성 여부 — 자식·손자 중 하나라도 현재 위치와 정확히
 * 일치하면 활성. displayYn='N'(라우팅 전용) 하위도 포함해야 상세 화면에서도 활성이 유지되므로
 * toVisibleTree를 거치지 않은 원본 노드를 넘길 것.
 */
export function hasActiveDescendant(node: MenuNode | null | undefined, pathname: string, search: string): boolean {
  if (!node) return false;
  return node.children.some(
    (c) => (c.routePath && isExactRoute(c.routePath, pathname, search)) || hasActiveDescendant(c, pathname, search),
  );
}

/**
 * routePath에 담긴 라우트 파라미터 자리(:fieldReportId 등 React Router 세그먼트 표기)를
 * targetId로 치환한다. 자리표시자가 없으면(목록 전용 화면 등 상세 라우트 자체가 없음)
 * targetId는 무시하고 routePath를 그대로 반환한다 — 화면 구조가 상세 진입을 지원하는지
 * 여부를 routePath 자체(TM_MENU.ROUTE_PATH, 실제 등록된 React Router 경로)로 판단한다.
 */
/**
 * 변수 자리가 없는 스케줄러 진입 경로(`/agents/scheduler?view=...`)는 siteId 쿼리로 현장을 지정한다.
 * 스케줄러 화면이 이 쿼리를 받아 해당 현장을 선택한다(예: 간트 WEB_SCHEDULE_GANTT).
 */
const SCHEDULER_ROUTE_PREFIX = '/agents/scheduler?';

function applyTargetId(routePath: string, targetId: number | string): string {
  return /:[A-Za-z0-9_]+/.test(routePath)
    ? routePath.replace(/:[A-Za-z0-9_]+/, String(targetId))
    : routePath;
}

/**
 * menuCode(+targetId) → 실제 이동 경로 (알림·현장요약 활동로그 라우팅 공용, 메뉴 코드 기반 전환).
 * menuCode가 없거나 트리에 없는 코드면 null(이동 없음) — 호출부가 그대로 "바로가기 없음"으로 처리한다.
 */
export function resolveMenuCodeRoute(
  tree: MenuNode[] | undefined,
  menuCode: string | null | undefined,
  targetId: number | string | null | undefined,
  siteId?: number | string | null,
): string | null {
  if (!menuCode) return null;
  const node = findByCode(tree, menuCode);
  if (!node?.routePath) return null;
  const withTarget = targetId != null ? applyTargetId(node.routePath, targetId) : node.routePath;
  if (withTarget.startsWith(SCHEDULER_ROUTE_PREFIX) && siteId != null && !/[?&]siteId=/.test(withTarget)) {
    return `${withTarget}&siteId=${encodeURIComponent(String(siteId))}`;
  }
  return withTarget;
}

/** 알림·현장요약 탭 액션 판정 결과 — route(이동) / other-platform(안내 메시지) / none(아무 것도 안 함) */
export type MenuCodeAction =
  | { kind: 'route'; path: string }
  | { kind: 'other-platform' }
  | { kind: 'none' };

/**
 * webMenuCode(+appMenuCode)로 클릭 시 동작을 판정한다.
 * - webMenuCode가 있고 트리에서 경로를 찾으면 route.
 * - webMenuCode가 없는데 appMenuCode는 있으면(=앱 전용 화면) other-platform.
 * - 둘 다 없으면(또는 webMenuCode가 있는데 트리에 없으면) none.
 */
export function resolveMenuCodeAction(
  tree: MenuNode[] | undefined,
  webMenuCode: string | null | undefined,
  appMenuCode: string | null | undefined,
  targetId: number | string | null | undefined,
  siteId?: number | string | null,
): MenuCodeAction {
  if (!webMenuCode) {
    return appMenuCode ? { kind: 'other-platform' } : { kind: 'none' };
  }
  const path = resolveMenuCodeRoute(tree, webMenuCode, targetId, siteId);
  return path ? { kind: 'route', path } : { kind: 'none' };
}

interface RouteEntry { node: MenuNode; parent: MenuNode | null; }

function buildRouteIndex(tree: MenuNode[] | undefined): RouteEntry[] {
  const out: RouteEntry[] = [];
  const walk = (nodes: MenuNode[], parent: MenuNode | null) => {
    for (const n of nodes) {
      if (n.routePath) out.push({ node: n, parent });
      walk(n.children, n);
    }
  };
  walk(tree ?? [], null);
  return out;
}

function matchRoutePath(routePath: string, pathname: string, search: string): boolean {
  return isExactRoute(routePath, pathname, search);
}

function crumbOf(entry: RouteEntry): string[] {
  const { node, parent } = entry;
  if (parent && (parent.typeCode === 'GROUP_HEADER' || parent.typeCode === 'FLYOUT_ROOT')) {
    return [parent.name, node.name];
  }
  return [node.name];
}

/**
 * 캐시된 메뉴 트리에서 routePath===pathname(+query)인 노드를 찾아 브레드크럼을 만든다
 * (설계서 §5.2 resolveMenuCrumb). 없으면 null — 호출부가 STATIC_CRUMBS 등으로 폴백한다.
 */
export function resolveMenuCrumb(tree: MenuNode[] | undefined, pathname: string, search: string): string[] | null {
  const entry = buildRouteIndex(tree).find((e) => matchRoutePath(e.node.routePath!, pathname, search));
  return entry ? crumbOf(entry) : null;
}

/**
 * TM_MENU에 없는 세부 라우트(`/partners/:id/edit` 등)를 위한 접두 일치 — 앞부분 마디만
 * 반환하고, 말단 화면명은 호출부의 STATIC_CRUMBS 등에서 이어붙인다.
 */
export function resolveMenuCrumbPrefix(tree: MenuNode[] | undefined, pathname: string): string[] | null {
  const candidates = buildRouteIndex(tree)
    .filter((e) => !e.node.routePath!.includes('?') && pathname.startsWith(`${e.node.routePath}/`))
    .sort((a, b) => b.node.routePath!.length - a.node.routePath!.length);
  return candidates.length ? crumbOf(candidates[0]) : null;
}
